import React, { useCallback, useRef, useState, useEffect, useContext } from "react";
import Webcam from "react-webcam";
import * as faceapi from 'face-api.js'
import fixWebmDuration from 'webm-duration-fix';
import { videoDimensions } from "../Camera.options";
import { STATEMENT_START_COUNTDOWN_SECONDS, StatementCountdown, VideoActions } from "./VideoSelfie.elements";
import { AccordionContext } from '../../Accordion/AccordionManager'
import { WrapperVideo } from "./VideoSelfie.styles";
import { CameraIsNotActive } from "../Camera.elements";

const DEFAULT_VIDEO_DURATION = 4050;

const LIVENESS_STATUS = {
    IDLE: 'IDLE',
    NEUTRAL_SUCCESS: 'NEUTRAL_SUCCESS',
    SMILE_SUCCESS: 'SMILE_SUCCESS',
    STATEMENT_CHECK: 'STATEMENT_CHECK',
    SUCCESS: 'SUCCESS',
}

export function VideoCamera({ onSend, statementRecordingTime = DEFAULT_VIDEO_DURATION, statementCheck = '' }) {
    const webcamRef = useRef(null)
    const detectorIdRef = useRef()
    const mediaRecorderRef = useRef(null)
    const [recordedChunks, setRecordedChunks] = useState([])
    const [message, setMessage] = useState('warning.loading')
    const [error, setError] = React.useState(false);
    const [actionView, setActionView] = useState('loading')
    const [statementCheckCountdown, setStatementCheckCountdown] = useState(0)

    const [activeTab, _setActiveTab] = useContext(AccordionContext)
    const isTabActive = activeTab === 'VIDEO_SELFIE'

    const videoRef = webcamRef.current?.video

    const startCountDown = () => {
        setMessage('warning.centerFace')
        setActionView('countdown')
    }

    const handleStatementCheckStart = () => {
        setMessage(statementCheck)
        setActionView('statementCheck')
        setStatementCheckCountdown(Math.round(statementRecordingTime/1000))
    }

    const handleWebcamLoaded = () => {
        Promise.all([
            faceapi.nets.tinyFaceDetector.loadFromUri(process.env.PUBLIC_URL + '/models'),
            faceapi.nets.faceLandmark68Net.loadFromUri(process.env.PUBLIC_URL + '/models'),
            faceapi.nets.faceRecognitionNet.loadFromUri(process.env.PUBLIC_URL + '/models'),
            faceapi.nets.faceExpressionNet.loadFromUri(process.env.PUBLIC_URL + '/models')
        ]).then(() => {
            setActionView('startRecording')
            setMessage('warning.pressToRecord')
        }).catch((e) => {
            console.error({e})
            // TODO: send notification
        })
    }

    const handleRecordingStart = () => setActionView('recording')

    const handleDataAvailable = useCallback(({ data }) => {
        if(data.size <= 0) return
        setRecordedChunks((prev) => prev.concat(data))
    }, [setRecordedChunks])
  
    const handleStartCapture = useCallback(() => {
        mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream)
        mediaRecorderRef.current.addEventListener("dataavailable", handleDataAvailable)
        mediaRecorderRef.current.start()
    }, [webcamRef, mediaRecorderRef, handleDataAvailable])
  
    const handleStopCaptureClick = useCallback(() => {
        const recorder = mediaRecorderRef.current
        recorder && recorder.state !== 'inactive' && recorder.stop()
        setActionView('finished')
        setMessage('warning.thankyou')
    }, [mediaRecorderRef])

    const handleFinishedCapture = useCallback(async () => {
        setActionView('sending')
        const blob = await fixWebmDuration(new Blob(recordedChunks, { type: 'video/webm' }))
        const url = URL.createObjectURL(blob)
        onSend(url)
    }, [recordedChunks])

    const handleFaceRecognition = useCallback(() => {
        let status = LIVENESS_STATUS.IDLE;
        const isStatus = (s) => status === s;
        const setStatus = (s) => status = s;

        let initTime, finishedTime, intervalId

        const faceApiOptions = new faceapi.TinyFaceDetectorOptions()
        const statementCheckDuration = statementRecordingTime + DEFAULT_VIDEO_DURATION + STATEMENT_START_COUNTDOWN_SECONDS*1000 + 50;
        
        intervalId = setInterval(async () => {
            detectorIdRef.current = intervalId
            const detections = await faceapi
                .detectSingleFace(videoRef,faceApiOptions)
                .withFaceLandmarks()
                .withFaceExpressions()
            if (detections) {
                const expressions = detections.expressions

                if (isStatus(LIVENESS_STATUS.IDLE) && expressions.neutral > 0.80) {
                    setStatus(LIVENESS_STATUS.NEUTRAL_SUCCESS)
                    initTime = performance.now()
                    handleStartCapture()
                    setMessage('warning.smile')
                }
                if (isStatus(LIVENESS_STATUS.NEUTRAL_SUCCESS) && expressions.happy > 0.80) {
                    setStatus(LIVENESS_STATUS.SMILE_SUCCESS)
                    setMessage('warning.thankyou')
                }
            }

            const endTime = performance.now()
            const recordingTime = endTime - initTime

            if(initTime && recordingTime >= DEFAULT_VIDEO_DURATION) {
                if(isStatus(LIVENESS_STATUS.SMILE_SUCCESS)){
                    if(statementCheck) {
                        setStatus(LIVENESS_STATUS.STATEMENT_CHECK)
                        setMessage('warning.statementCheck')
                        setActionView('statementCheckCountdown')
                    } else {
                        setStatus(LIVENESS_STATUS.SUCCESS)
                        setMessage('warning.thankyou')
                    }
                }
                if(isStatus(LIVENESS_STATUS.NEUTRAL_SUCCESS)) {
                        initTime = null
                        clearInterval(intervalId)
                        setRecordedChunks([])
                        setMessage('warning.retryDetector')
                        setActionView('countdown')  
                }
            }

            if(isStatus(LIVENESS_STATUS.STATEMENT_CHECK)) {
                if(recordingTime >= statementCheckDuration + 500) {
                    setStatus(LIVENESS_STATUS.SUCCESS)
                    setMessage('warning.thankyou')
                }
            }

            if(isStatus(LIVENESS_STATUS.SUCCESS)) {
                !finishedTime && (finishedTime = endTime)
                if(endTime - finishedTime >= 2500) {
                    clearInterval(intervalId)
                    handleStopCaptureClick()
                }
            }
        }, 200)
    }, [videoRef])
  
    useEffect(() => {
        actionView === 'recording' && handleFaceRecognition()
    }, [actionView, handleFaceRecognition])

    useEffect(() => {
        actionView === 'finished' && !!recordedChunks.length && handleFinishedCapture()
    }, [actionView, recordedChunks.length, handleFinishedCapture])

    useEffect(() => {
        return () => clearInterval(detectorIdRef.current)
    }, [])

    useEffect(() => {
        if(!isTabActive) {
            setActionView('startRecording')
            setStatementCheckCountdown(null)
            clearInterval(detectorIdRef.current)
            setRecordedChunks([])
        }
    }, [isTabActive])


  if(error) {
    return <CameraIsNotActive refreshCameraSettings={() => setError(false)}/>
  }

    return (
        <WrapperVideo>
            <Webcam
                audio={true}
                muted={true}
                ref={webcamRef}
                mirrored={true}
                height={videoDimensions.height}
                width={videoDimensions.width}
                onPlaying={handleWebcamLoaded}
                onUserMediaError={() => setError(true)}
                style={{
                    height: videoDimensions.height,
                    width: videoDimensions.width,
                    objectFit: "cover",
                    position: "absolute"
                 }}
            />
            <VideoActions
              handleRecordClick={startCountDown}
              onStartRecording={handleRecordingStart}
              onStatementCheckStart={handleStatementCheckStart}
              isTabActive={isTabActive}
              actionView={actionView}
              videoDuration={statementRecordingTime + 6}
              message={message}
            />
            <StatementCountdown
                maxSeconds={Math.round(statementRecordingTime / 1000 + 1)}
                countdownSeconds={statementCheckCountdown}
            />
        </WrapperVideo>
    )
  }